import { CalendarDays, ChevronRight, TriangleAlert, User } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { useProjectIssues } from "@/hooks/use-issues";
import { formatDueDate, overdueLabel, personName } from "@/lib/issues";
import { cn } from "@/lib/utils";
import type { ProjectIssue } from "@/types/api";

interface RecordIssuesBandProps {
  projectId: string;
  recordId: string;
  /**
   * Id of an issue raised moments ago. Tinted while set — the durable half of
   * the post-raise feedback, since the toast can be missed in sunlight but a
   * row appearing under the photo cannot.
   */
  highlightIssueId?: string | null;
  /**
   * Host layout only — the band lives inside the padded metadata column and
   * bleeds back out to its edges. Nothing about the band's own chrome.
   */
  className?: string;
}

/**
 * Issues raised against one record, rendered under its photos.
 *
 * Mounted only by callers inside an authenticated project — see
 * [[record-issues-band.spec]]. This component fetches, and the public share
 * page renders the same host modal against endpoints that answer a project
 * read with a 401, so it must never mount there.
 */
export function RecordIssuesBand({
  projectId,
  recordId,
  highlightIssueId,
  className,
}: RecordIssuesBandProps) {
  const navigate = useNavigate();
  const { data, isError } = useProjectIssues(projectId, { record: recordId });

  // `useProjectIssues` is an infinite query with a `select` that flattens the
  // pages — the rows are on `issues`, not `results`.
  const issues = data?.issues ?? [];
  // A failed or empty read renders nothing at all. The photo is what the user
  // opened; neither an error strip nor an empty state earns space above it.
  if (isError || issues.length === 0) return null;

  // Closed sink below the live ones, newest-first order preserved within each
  // group. They stay in the list because "was this already dealt with?" is
  // answered by a closed issue as often as an open one.
  const ordered = [
    ...issues.filter((issue: ProjectIssue) => issue.status !== "closed"),
    ...issues.filter((issue: ProjectIssue) => issue.status === "closed"),
  ];

  return (
    <div
      className={cn(
        "border-b border-border bg-card px-4 pb-2 pt-3.5",
        className,
      )}
    >
      <span className="block pb-0.5 text-sm font-medium">Issues</span>
      {ordered.map((issue, index) => (
        <IssueRow
          key={issue.id}
          issue={issue}
          isFirst={index === 0}
          isHighlighted={issue.id === highlightIssueId}
          onOpen={() =>
            void navigate({
              to: "/projects/$projectId/issues/$issueId",
              params: { projectId, issueId: issue.id },
            })
          }
        />
      ))}
    </div>
  );
}

function IssueRow({
  issue,
  isFirst,
  isHighlighted,
  onOpen,
}: {
  issue: ProjectIssue;
  isFirst: boolean;
  isHighlighted: boolean;
  onOpen: () => void;
}) {
  const isClosed = issue.status === "closed";
  // overdueLabel already returns null for a closed issue — finished late is
  // finished — so the due line falls back to the plain date for those.
  const overdue = overdueLabel(issue);
  const due = overdue ?? formatDueDate(issue.due_date);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full items-start gap-2.5 py-2.5 text-left",
        !isFirst && "border-t border-border",
        isHighlighted && "-mx-2.5 rounded-lg bg-primary/15 px-2.5",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/*
          Up to three lines, then an ellipsis. One line truncated most site
          titles ("SWC to cut these 2 frame for the actu…"), which is the half
          that says what the issue is; three lines fits a typical one whole and
          the row grows to hug it.
        */}
        <span
          className={cn(
            "line-clamp-3 break-words text-sm",
            isClosed ? "text-muted-foreground" : "font-medium",
          )}
        >
          {issue.title}
        </span>
        {/*
          Same icon vocabulary as the issues list: a person for the assignee, a
          warning triangle for an overdue date. The calendar is this band's
          addition, so a due date reads as a date without the word "Due".
        */}
        <span className="flex items-center gap-2 overflow-hidden text-xs">
          <span
            className={cn(
              "inline-flex min-w-0 items-center gap-1",
              issue.assigned_to
                ? "text-muted-foreground"
                : "text-muted-foreground/70",
            )}
          >
            <User className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {issue.assigned_to ? personName(issue.assigned_to) : "Unassigned"}
            </span>
          </span>
          {due ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 whitespace-nowrap",
                overdue
                  ? "font-medium text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {overdue ? (
                <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
              ) : (
                <CalendarDays className="h-3 w-3 shrink-0" aria-hidden="true" />
              )}
              {due}
            </span>
          ) : null}
        </span>
      </span>
      {/*
        Pinned to the first line rather than centred: against a three-line
        title a centred pill drifts down beside the assignee line and stops
        reading as this row's status.
      */}
      <span className="flex shrink-0 items-center gap-2.5 pt-0.5">
        <IssueStatusPill status={issue.status} />
        <ChevronRight
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      </span>
    </button>
  );
}
